import React, { useEffect, useState } from "react";
import axios from "axios";
import Select from "react-select";
import { useHistory, useParams } from "react-router-dom";
import { Form, InputGroup } from "react-bootstrap";
import RequiredLabel from "../../CustomCommons/RequiredLabel";
import { BASE_URL } from "../../Const/Url";
import { showToast } from "../../../utils/ToastHelper";
import { logout_func2 } from "../../Const/logoutCommon";
import SelectOpenWithPagginationDynamic from "../../Const/SelectOpenWithPagginationDynamic";
import { handleSelectAll } from "../../Const/Status";
import Loader from "../../CustomCommons/Loader";

const EditBestCat = () => {
  const history = useHistory();
  const { slug } = useParams();

  const [isFetching, setIsFetching] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [, setOnlogoutHide] = useState(0);

  const [title, setTitle] = useState("");
  const [mediaType, setMediaType] = useState(0); // 0 = image, 1 = video
  const [isActive, setIsActive] = useState(true);

  const [bannerId, setBannerId] = useState(null);
  const [bannerName, setBannerName] = useState("");
  const [bannerPreview, setBannerPreview] = useState("");

  const [videoId, setVideoId] = useState(null);
  const [videoName, setVideoName] = useState("");
  const [videoPreview, setVideoPreview] = useState("");

  const [categoryQuery, setCategoryQuery] = useState("");
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);

  const [productQuery, setProductQuery] = useState("");
  const [productList, setProductList] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);

  const uploadDocument = (file, onSuccess, docType = 0) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("document", file);
    formData.append("doc_type", docType);

    axios
      .post(`${BASE_URL}api/v1/auth/documents/upload/`, formData)
      .then((res) => {
        if (res?.data?.status) {
          onSuccess(res?.data?.data);
          showToast("success", "File uploaded successfully");
        } else {
          showToast("error", "Failed to upload file");
        }
      })
      .catch((err) => {
        const message =
          err?.response?.data?.errors?.document?.[0] ||
          err?.response?.data?.message ||
          "Upload failed";
        showToast("error", message);
      });
  };

  const handleBannerUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    uploadDocument(
      file,
      (data) => {
        setBannerId(data?.id);
        setBannerName(file.name);
        setBannerPreview(data?.doc_url || data?.resized_doc_url || "");
      },
      0
    );
  };

  const handleVideoUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    uploadDocument(
      file,
      (data) => {
        setVideoId(data?.id);
        setVideoName(file.name);
      },
      1
    );
    setVideoPreview("");
  };

  const getCategories = (query = "") => {
    const params = new URLSearchParams();
    params.append("limit", 20);
    params.append("is_website", true);
    if (query) {
      params.append("query", query);
    }

    axios
      .get(
        `${BASE_URL}api/v1/inventory/inventory/category/?${params.toString()}`
      )
      .then((res) => {
        const results = res?.data?.data?.results || [];
        const formatted = results.map((item) => ({
          label: item?.name,
          value: item?.id,
        }));
        setCategoryOptions(formatted);
      })
      .catch(() => {});
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      getCategories(categoryQuery);
    }, 300);
    return () => clearTimeout(debounce);
  }, [categoryQuery]);

  const fetchDetails = React.useCallback(() => {
    if (!slug) {
      showToast("error", "Missing category identifier.");
      history.push("/website-settings/top-best-category-list");
      return;
    }

    setIsFetching(true);
    axios
      .get(
        `${BASE_URL}api/v1/frontend/admin/v2/custom-best-categories/${slug}/`
      )
      .then((res) => {
        const data = res?.data?.data;
        if (!data) {
          showToast("error", "Failed to load category details.");
          history.push("/website-settings/top-best-category-list");
          return;
        }

        setTitle(data?.title || "");
        setIsActive(data?.is_active ?? true);

        // Image/video ids
        setBannerId(data?.banner || null);
        setVideoId(data?.video || null);

        // Image preview/name
        if (data?.banner_details?.doc_url) {
          setBannerPreview(data.banner_details.doc_url);
          setBannerName(data.banner_details.document || "");
        }

        // Video preview/name
        if (data?.video_details?.doc_url) {
          setVideoPreview(data.video_details.doc_url);
        }
        if (data?.video_details?.document) {
          setVideoName(data.video_details.document);
        }

        // Decide media type based on available media
        const hasVideoDetails =
          !!data?.video_details &&
          (!!data.video_details.doc_url || !!data.video_details.document);
        const hasBannerDetails =
          !!data?.banner_details &&
          (!!data.banner_details.doc_url || !!data.banner_details.document);

        if (hasVideoDetails) {
          setMediaType(1); // Video
        } else if (hasBannerDetails) {
          setMediaType(0); // Image
        } else {
          setMediaType(data?.media_type ?? 0);
        }

        const prefCategories =
          data?.category_details?.map((item) => ({
            label: item?.name,
            value: item?.id,
          })) || [];
        setSelectedCategories(prefCategories);
        setCategoryOptions((prev) => {
          const combined = [...prev];
          prefCategories.forEach((cat) => {
            if (!combined.find((opt) => opt.value === cat.value)) {
              combined.push(cat);
            }
          });
          return combined;
        });

        const prefProducts =
          data?.products_details?.map((item) => ({
            label: item?.name,
            value: item?.id,
          })) || [];
        setSelectedProducts(prefProducts);

        setProductList((prev) => {
          const combined = [...prev];
          prefProducts.forEach((prod) => {
            if (!combined.find((opt) => opt.value === prod.value)) {
              combined.push(prod);
            }
          });
          return combined;
        });
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 401) {
          logout_func2(setOnlogoutHide, history);
          return;
        }
        showToast("error", "Unable to fetch category details.");
        history.push("/website-settings/top-best-category-list");
      })
      .finally(() => setIsFetching(false));
  }, [slug, history]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const validateForm = () => {
    if (!title.trim()) {
      showToast("error", "Title is required.");
      return false;
    }
    if (!selectedCategories.length) {
      showToast("error", "Please select at least one category.");
      return false;
    }
    if (Number(mediaType) === 0) {
      if (!bannerId) {
        showToast("error", "Please upload a banner image.");
        return false;
      }
    }
    if (Number(mediaType) === 1) {
      if (!videoId) {
        showToast("error", "Please upload a banner video.");
        return false;
      }
    }
    return true;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    const payload = {
      title: title.trim(),
      is_active: isActive,
      media_type: Number(mediaType),
      banner: Number(mediaType) === 0 ? bannerId : null,
      video: Number(mediaType) === 1 ? videoId : null,
      categories: selectedCategories.map((item) => item.value),
      products: selectedProducts.length
        ? selectedProducts.map((item) => item.value)
        : [],
    };

    setIsSubmitting(true);
    axios
      .patch(
        `${BASE_URL}api/v1/frontend/admin/v2/custom-best-categories/${slug}/`,
        payload
      )
      .then(() => {
        showToast("success", "Top best category updated successfully.");
        history.push("/website-settings/top-best-category-list");
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 401) {
          logout_func2(setOnlogoutHide, history);
          return;
        }
        const message =
          err?.response?.data?.errors?.non_field_errors?.[0] ||
          err?.response?.data?.message ||
          "Failed to update top best category. Please try again.";
        showToast("error", message);
      })
      .finally(() => setIsSubmitting(false));
  };

  const handleCancel = () => {
    history.push("/website-settings/top-best-category-list");
  };

  if (isFetching) {
    return <Loader />;
  }

  return (
    <div className="categorey-parent new-categorey-parent an">
      <div className="bg-white add-div">
        <div
          className="d-flex align-items-center justify-content-between"
          style={{ padding: "20px 35px", border: "0.5px solid #E0E0E0" }}
        >
          <h3 style={{ fontSize: "23px", marginTop: "6px" }}>
            Edit Top Best Category
          </h3>
        </div>

        <div
          style={{
            padding: "20px 35px",
            borderBottom: "0.5px solid #E0E0E0",
            borderRight: "0.5px solid #E0E0E0",
            borderLeft: "0.5px solid #E0E0E0",
          }}
          className="row"
        >
          <div className="col-12 col-md-6">
            <RequiredLabel text="Title" />
            <InputGroup className="mb-3" style={{ height: "47px" }}>
              <Form.Control
                aria-label="Title"
                style={{ backgroundColor: "#FAFAFA" }}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </InputGroup>
          </div>

          <div className="col-12 col-md-6">
            <RequiredLabel text="Media Type" />
            <InputGroup className="mb-3" style={{ height: "47px" }}>
              <Form.Select
                aria-label="Media Type"
                style={{ backgroundColor: "#FAFAFA" }}
                value={mediaType}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  setMediaType(value);
                  if (value === 0) {
                    setVideoId(null);
                    setVideoName("");
                    setVideoPreview("");
                  } else {
                    setBannerId(null);
                    setBannerName("");
                    setBannerPreview("");
                  }
                }}
              >
                <option value={0}>Image</option>
                <option value={1}>Video</option>
              </Form.Select>
            </InputGroup>
          </div>

          <div className="col-12">
            <RequiredLabel text="Categories" />
            <div className="position-relative mb-3">
              <Select
                isMulti
                placeholder="Select categories"
                value={selectedCategories}
                onChange={(value) => setSelectedCategories(value || [])}
                onInputChange={(value) => setCategoryQuery(value)}
                options={categoryOptions}
              />
            </div>
          </div>

          <div className="col-12">
            <RequiredLabel text="Products (Optional)" />
            <div className="position-relative mb-3">
              <SelectOpenWithPagginationDynamic
                handleMultiInputChange={(value) => setSelectedProducts(value)}
                inputProduct={(value) => setProductQuery(value)}
                ProductsList={productList}
                defaultValue={selectedProducts}
                setProductsList={setProductList}
                inputQuery={productQuery}
                selectAll={() =>
                  handleSelectAll(productList, setSelectedProducts)
                }
                url1={`api/v1/inventory/inventory/product_list_for_offer/?is_website=true&limit=20`}
                url2={`api/v1/inventory/inventory/product_list_for_offer/?query=${productQuery}&is_website=true&limit=20`}
              />
            </div>
          </div>

          {Number(mediaType) === 0 && (
            <div className="col-12">
              <RequiredLabel text="Banner Image" />
              <p style={{ marginTop: "10px", color: "red" }}>
                NB: Upload image in (height-420px, width-1920px) for Banner.
              </p>
              <div
                className="w-100 browse-main d-flex align-items-center"
                style={{
                  height: "55px",
                  border: "1px solid #E0E0E0",
                  borderRadius: "7px",
                  overflowX: "hidden",
                }}
              >
                <p className="my-2 ms-2" style={{ color: "#4F4F4F" }}>
                  {bannerName ? "File Chosen" : "No File Chosen"}
                </p>
                <div className="file-up">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleBannerUpload}
                  />
                  <p>Browse</p>
                </div>
              </div>
              {bannerPreview && (
                <div className="my-2">
                  <img
                    height={90}
                    width={90}
                    src={bannerPreview}
                    alt="banner"
                  />
                </div>
              )}
            </div>
          )}

          {Number(mediaType) === 1 && (
            <div className="col-12">
              <RequiredLabel text="Banner Video" />
              <div
                className="w-100 browse-main d-flex align-items-center"
                style={{
                  height: "55px",
                  border: "1px solid #E0E0E0",
                  borderRadius: "7px",
                  overflowX: "hidden",
                }}
              >
                <p className="my-2 ms-2" style={{ color: "#4F4F4F" }}>
                  {videoName ? "File Chosen" : "No File Chosen"}
                </p>
                <div className="file-up">
                  <input
                    type="file"
                    accept="video/*"
                    onChange={handleVideoUpload}
                  />
                  <p>Browse</p>
                </div>
              </div>
              {videoPreview && (
                <div className="my-2">
                  <video height={120} width={160} src={videoPreview} controls />
                </div>
              )}
            </div>
          )}

          <div className="col-12 mt-4">
            <h5>Status</h5>
            <p style={{ color: "#8E8E93" }}>( Toggle visibility )</p>
            <div
              className="d-flex justify-content-between align-items-center py-3 px-3"
              style={{ backgroundColor: "#F2F2F7", borderRadius: "8px" }}
            >
              <span>{isActive ? "Active" : "Inactive"}</span>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
              </div>
            </div>
          </div>

          <div className="mt-4">
            <button
              onClick={handleSubmit}
              className="save-btn me-2 mt-2"
              style={{ background: "#000", color: "white" }}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Updating..." : "Update"}
            </button>
            <button
              onClick={handleCancel}
              className="save-btn mt-2"
              style={{ border: ".4px solid #E0E0E0 !important" }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditBestCat;
